import type { Language } from '../i18n/LanguageContext';

// Type for fields that can be either a string or an object with language keys
export type TranslatableField<T> = T | { de: T; en: T };

/**
 * Extract translated value from a field that can be either:
 * - A plain string (backward compatible)
 * - An object with 'de' and 'en' keys
 * 
 * @param field - The field to extract from
 * @param language - The target language
 * @returns The translated value, or the original if it's a string
 */
export function getTranslated<T>(
  field: TranslatableField<T> | undefined,
  language: Language
): T | undefined {
  if (field === undefined || field === null) {
    return undefined;
  }
  
  // Check if it's an object with language keys
  if (typeof field === 'object' && field !== null && 'de' in field && 'en' in field) {
    const translatable = field as { de: T; en: T };
    // Return requested language, fallback to German
    return translatable[language] || translatable.de;
  }
  
  // It's a plain value (backward compatible)
  return field as T;
}

/**
 * Get translated string with fallback
 */
export function getTranslatedString(
  field: TranslatableField<string> | undefined,
  language: Language,
  fallback: string = ''
): string {
  const result = getTranslated(field, language);
  return result || fallback;
}
